/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { Gavel, MessageCircle, Shield, AlertCircle, CheckCircle2, Clock, Copy, KeyRound, FileText, Star } from "lucide-react";
import OrderChatPanel from "../components/OrderChatPanel";

const STATUS_STYLE = {
  pending: "tag",
  paid: "tag tag-accent",
  delivered: "tag !border-[#FFD700]/50 !text-[#FFD700] !bg-[#FFD700]/10",
  completed: "tag !border-[#00FF66]/50 !text-[#00FF66] !bg-[#00FF66]/10",
  disputed: "tag !border-[#FF003C]/50 !text-[#FF003C] !bg-[#FF003C]/10",
  refunded: "tag !border-white/50 !text-white !bg-white/10",
};

const DELIVERY_LABEL = {
  pending: "Menunggu dikirim",
  shipped: "Sudah dikirim · Garansi aktif",
  completed: "Selesai",
  disputed: "Komplain · Menunggu admin",
  refunded: "Dana dikembalikan",
};

const formatRemaining = (untilIso) => {
  if (!untilIso) return null;
  const ms = new Date(untilIso) - new Date();
  if (ms <= 0) return "Garansi habis";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `${d} hari ${h} jam`;
  return `${h} jam`;
};

const AUCTION_STATUS_STYLE = {
  live: "tag !border-[#7C6EFF]/50 !text-[#7C6EFF] !bg-[#7C6EFF]/10",
  frozen: "tag !border-[#7C6EFF]/50 !text-[#7C6EFF] !bg-[#7C6EFF]/10",
  ended: "tag !border-[#FFD700]/50 !text-[#FFD700] !bg-[#FFD700]/10",
  sold: "tag !border-[#00FF66]/50 !text-[#00FF66] !bg-[#00FF66]/10",
  expired: "tag !border-white/30 !text-white/50",
  cancelled: "tag !border-white/30 !text-white/50",
};

export default function Account() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [garansiRequests, setGaransiRequests] = useState([]);
  const [tab, setTab] = useState("orders");
  const [extendModal, setExtendModal] = useState(null);
  const [complainModal, setComplainModal] = useState(null);
  const [chatModal, setChatModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [reason, setReason] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  const refresh = () => {
    api.get("/orders/me").then((r) => setOrders(r.data));
    api.get("/auctions/my-bids").then((r) => setMyBids(r.data));
    api.get("/garansi/requests").then((r) => setGaransiRequests(r.data));
  };

  useEffect(() => {
    if (user && user !== false) refresh();
  }, [user]);

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  const confirmReceipt = async (id) => {
    try {
      await api.post(`/orders/${id}/confirm-receipt`);
      toast.success("Receipt confirmed. Terima kasih!");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const copyCode = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Kode berhasil disalin");
    } catch {
      toast.error("Gagal menyalin kode");
    }
  };

  const submitExtend = async (e) => {
    e.preventDefault();
    try {
      if (extendModal.kind === "order") {
        await api.post(`/orders/${extendModal.id}/extend-warranty`, { reason });
        toast.success("Garansi diperpanjang +7 hari");
      } else {
        await api.post("/garansi/extend-request", { auction_id: extendModal.id, reason });
        toast.success("Request perpanjang garansi terkirim. Admin akan review.");
      }
      setExtendModal(null);
      setReason("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitComplain = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${complainModal.id}/complain`, { reason });
      toast.success("Komplain dikirim. Admin akan menangani.");
      setComplainModal(null);
      setReason("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) {
      toast.error("Rating wajib dipilih (1-5 bintang)");
      return;
    }
    try {
      await api.post(`/orders/${reviewModal.id}/review`, {
        rating,
        comment: comment.trim(),
      });
      toast.success("Ulasan berhasil dikirim. Terima kasih!");
      setReviewModal(null);
      setRating(0);
      setHoverRating(0);
      setComment("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim ulasan");
    }
  };

  const wonAuctions = myBids.filter((b) => b.auction.winner_user_id === user.id || (b.auction.current_bid_user_id === user.id && b.auction.status === "sold"));

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-4 md:px-8 py-12">
      <span className="tag tag-accent">// ACCOUNT</span>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3 mb-2">Hi, {user.name}</h1>
      <p className="text-white/55 mb-8">{user.email} · <span className="uppercase tracking-widest text-[10px]">{user.role}</span></p>

      <div className="border-b border-white/10 mb-6 flex gap-1 flex-wrap">
        {[
          { id: "orders", label: "Pesanan" },
          { id: "bids", label: `My Bids (${myBids.length})` },
          { id: "garansi", label: `Garansi (${garansiRequests.length})` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-bold ${tab === t.id ? "text-[#00F0FF] border-b-2 border-[#00F0FF]" : "text-white/50 hover:text-white/80"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" && (orders.length === 0 ? (
        <div className="border border-white/10 p-12 text-center text-white/60" data-testid="no-orders">
          Belum ada pesanan. <Link to="/browse" className="text-[#00F0FF] hover:underline">Mulai belanja</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const dStatus = o.delivery_status || (o.status === "completed" ? "completed" : (o.status === "delivered" ? "shipped" : "pending"));
            const remaining = formatRemaining(o.warranty_until);
            const canConfirm = dStatus === "shipped";
            const canComplain = ["shipped", "pending"].includes(dStatus) && !o.escrow_released;
            const canExtend = dStatus === "shipped" && Number(o.extended_count || 0) < 2;
            return (
            <div key={o.id} className="border border-white/10 p-4" data-testid={`order-${o.id}`}>
              <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">Order</span>
                  <p className="font-mono text-xs text-white">{o.id.slice(0, 12)}...</p>
                </div>
                <div className="flex gap-2 items-center">
                  <span className={STATUS_STYLE[dStatus] || "tag"}>{(DELIVERY_LABEL[dStatus] || dStatus).toUpperCase()}</span>
                  <Link
                    to={`/orders/${o.id}`}
                    data-testid={`order-detail-link-${o.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-white/70 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[10px] uppercase font-bold tracking-widest"
                  >
                    <FileText className="w-3 h-3" /> Detail
                  </Link>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                {o.items.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-white/70">
                    <span>{it.title} × {it.quantity}</span>
                    <span>{formatIDR(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>

              {o.items.some((it) => (it.delivered_codes || []).length > 0) && (
                <div className="mt-4 border border-[#00FF66]/35 bg-[#00FF66]/5 p-4" data-testid={`digital-codes-${o.id}`}>
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                    <KeyRound className="h-4 w-4" /> Kode Voucher / Akun
                  </p>
                  <p className="mt-1 text-[11px] text-white/50">Pembayaran berhasil. Kode berikut otomatis diberikan dan pesanan digital telah selesai.</p>
                  <div className="mt-3 space-y-3">
                    {o.items.flatMap((it) => (it.delivered_codes || []).map((entry, index) => {
                      const value = typeof entry === "string" ? entry : entry.code;
                      return (
                        <div key={`${it.product_id}-${entry.id || index}`} className="border border-white/10 bg-black/60 p-3">
                          <p className="mb-2 text-[10px] uppercase tracking-widest text-white/45">{it.title} · Kode {index + 1}</p>
                          <div className="flex items-start gap-2">
                            <pre className="min-w-0 flex-1 whitespace-pre-wrap break-all font-mono text-sm text-white" data-testid={`delivered-code-${o.id}-${index}`}>{value}</pre>
                            <button type="button" onClick={() => copyCode(value)} className="shrink-0 border border-[#00F0FF]/40 p-2 text-[#00F0FF] hover:bg-[#00F0FF]/10" aria-label="Salin kode">
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    }))}
                  </div>
                </div>
              )}

              {/* Warranty / escrow info */}
              {dStatus === "shipped" && o.warranty_until && (
                <div className="mt-3 p-3 border border-[#FFD700]/30 bg-[#FFD700]/5 text-xs flex items-center gap-2" data-testid={`warranty-info-${o.id}`}>
                  <Clock className="w-4 h-4 text-[#FFD700]" />
                  <span className="text-[#FFD700]">Garansi tersisa: <strong>{remaining}</strong></span>
                  {o.extended_count > 0 && <span className="text-white/50">· Diperpanjang {o.extended_count}/2</span>}
                </div>
              )}
              {dStatus === "disputed" && (
                <div className="mt-3 p-3 border border-[#FF003C]/30 bg-[#FF003C]/5 text-xs" data-testid={`dispute-info-${o.id}`}>
                  <p className="text-[#FF003C] font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Komplain sedang ditangani admin</p>
                  {o.dispute_reason && <p className="text-white/60 mt-1">Alasan: "{o.dispute_reason}"</p>}
                </div>
              )}
              {dStatus === "refunded" && (
                <div className="mt-3 p-3 border border-[#00FF66]/30 bg-[#00FF66]/5 text-xs text-[#00FF66]" data-testid={`refunded-info-${o.id}`}>
                  Dana sudah dikembalikan ke wallet Anda
                </div>
              )}

              <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <span className="text-xs uppercase tracking-widest text-white/60">Total</span>
                  <p className="font-display font-bold text-[#00F0FF]">{formatIDR(o.total)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Chat button — available for any active order */}
                  {!['pending'].includes(o.status) && !o.digital_fulfillment && (
                    <button data-testid={`chat-order-${o.id}`} onClick={() => setChatModal(o)} className="inline-flex items-center gap-1 px-3 py-2 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase font-bold tracking-widest">
                      <MessageCircle className="w-3 h-3" /> Chat Seller
                    </button>
                  )}
                  {/* Review button — only for completed orders that haven't been reviewed */}
                  {dStatus === "completed" && !o.reviewed && (
                    <button
                      type="button"
                      data-testid={`review-order-${o.id}`}
                      onClick={() => {
                        setReviewModal(o);
                        setRating(0);
                        setHoverRating(0);
                        setComment("");
                      }}
                      className="inline-flex items-center gap-1 px-3 py-2 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs uppercase font-bold tracking-widest"
                    >
                      <Star className="w-3 h-3" /> Berikan Ulasan
                    </button>
                  )}
                  {dStatus === "completed" && o.reviewed && (
                    <span className="inline-flex items-center gap-1 px-3 py-2 border border-[#00FF66]/30 text-[#00FF66] text-xs uppercase font-bold tracking-widest">
                      <CheckCircle2 className="w-3 h-3" /> Sudah Diulas
                    </span>
                  )}
                  {canExtend && (
                    <button data-testid={`extend-order-${o.id}`} onClick={() => { setExtendModal({ ...o, kind: "order" }); setReason(""); }} className="inline-flex items-center gap-1 px-3 py-2 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs uppercase font-bold tracking-widest">
                      <Shield className="w-3 h-3" /> Perpanjang +7h
                    </button>
                  )}
                  {canComplain && (
                    <button data-testid={`complain-order-${o.id}`} onClick={() => { setComplainModal(o); setReason(""); }} className="inline-flex items-center gap-1 px-3 py-2 border border-[#FF003C]/50 text-[#FF003C] hover:bg-[#FF003C]/10 text-xs uppercase font-bold tracking-widest">
                      <AlertCircle className="w-3 h-3" /> Komplain
                    </button>
                  )}
                  {canConfirm && (
                    <button data-testid={`confirm-receipt-${o.id}`} onClick={() => confirmReceipt(o.id)} className="inline-flex items-center gap-1 px-3 py-2 bg-[#00FF66] text-black hover:bg-[#33FF88] text-xs uppercase font-bold tracking-widest">
                      <CheckCircle2 className="w-3 h-3" /> Pesanan Diterima
                    </button>
                  )}
                  {dStatus === "pending" && <span className="text-xs uppercase tracking-widest text-white/40">Menunggu seller mengirim</span>}
                  {dStatus === "completed" && <span className="text-xs uppercase tracking-widest text-[#00FF66]">Selesai</span>}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ))}

      {tab === "bids" && (myBids.length === 0 ? (
        <div className="border border-white/10 p-12 text-center text-white/60" data-testid="no-bids">
          Belum ada bid. <Link to="/auction" className="text-[#7C6EFF] hover:underline">Lihat auction live</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {myBids.map((b) => {
            const a = b.auction;
            const isWinning = a.current_bid_user_id === user.id;
            const won = a.status === "sold" && a.winner_user_id === user.id;
            return (
              <div key={b.bid.id} className="border border-white/10 p-4" data-testid={`bid-row-${b.bid.id}`}>
                <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate flex items-center gap-2">
                      <Gavel className="w-4 h-4 text-[#7C6EFF]" /> {a.title}
                    </h3>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                      Your bid: {formatIDR(b.bid.amount)} · {new Date(b.bid.created_at).toLocaleString("id-ID")}
                    </p>
                  </div>
                  <span className={AUCTION_STATUS_STYLE[a.status] || "tag"}>{a.status.toUpperCase()}</span>
                </div>
                <div className="flex items-center justify-between border-t border-white/10 pt-3 flex-wrap gap-2">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">{isWinning ? "Kamu memimpin" : "Current bid"}</span>
                    <p className="font-display font-bold text-[#00F0FF]">{formatIDR(a.current_bid_amount || 0)}</p>
                  </div>
                  <div className="flex gap-2">
                    {won && a.is_instant && a.account_details && (
                      <details className="cursor-pointer" data-testid={`instant-details-${a.id}`}>
                        <summary className="px-3 py-2 border border-[#00FF66]/40 text-[#00FF66] text-xs uppercase tracking-widest font-bold">⚡ Lihat Akun</summary>
                        <pre className="mt-2 p-3 bg-black border border-[#00FF66]/30 text-[#00FF66] text-xs font-mono max-w-sm overflow-x-auto whitespace-pre-wrap">{a.account_details}</pre>
                      </details>
                    )}
                    {won && (
                      <Link to={`/chat?to=${a.seller_id}&auction=${a.id}`} data-testid={`chat-seller-${a.id}`} className="inline-flex items-center gap-2 px-3 py-2 bg-[#00F0FF] text-black text-xs uppercase font-bold tracking-widest">
                        <MessageCircle className="w-3 h-3" /> Chat Seller
                      </Link>
                    )}
                    {won && !a.payout_done && (
                      <button
                        data-testid={`confirm-receipt-auction-${a.id}`}
                        onClick={async () => {
                          if (!window.confirm("Konfirmasi pesanan auction diterima? Saldo akan langsung cair ke seller.")) return;
                          try {
                            await api.post(`/auctions/${a.id}/confirm-delivery`);
                            toast.success("Pesanan dikonfirmasi. Saldo cair ke seller.");
                            refresh();
                          } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 bg-[#00FF66] text-black hover:bg-[#33FF88] text-xs uppercase font-bold tracking-widest"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Pesanan Diterima
                      </button>
                    )}
                    {won && (
                      <button
                        data-testid={`extend-garansi-${a.id}`}
                        onClick={() => setExtendModal(a)}
                        className="px-3 py-2 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs uppercase font-bold tracking-widest"
                      >
                        <Shield className="inline w-3 h-3 mr-1" /> Extend Garansi
                      </button>
                    )}
                    {a.status === "live" && !isWinning && (
                      <Link to="/auction" className="px-3 py-2 border border-[#7C6EFF]/60 text-[#7C6EFF] text-xs uppercase font-bold tracking-widest">Counter Bid</Link>
                    )}
                  </div>
                </div>
                {won && a.garansi_expires_at && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                    <Shield className="w-3 h-3 text-[#00FF66]" /> Garansi sampai {new Date(a.garansi_expires_at).toLocaleString("id-ID")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {tab === "garansi" && (garansiRequests.length === 0 ? (
        <div className="border border-white/10 p-12 text-center text-white/60" data-testid="no-garansi">
          Belum ada request perpanjang garansi.
        </div>
      ) : (
        <div className="space-y-3">
          {garansiRequests.map((r) => (
            <div key={r.id} className="border border-white/10 p-4" data-testid={`garansi-${r.id}`}>
              <div className="flex justify-between mb-2">
                <h3 className="font-bold text-white">{r.auction_title}</h3>
                <span className={`tag ${r.status === "pending" ? "!border-[#FFD700]/50 !text-[#FFD700] !bg-[#FFD700]/10" : r.status === "approved" ? "!border-[#00FF66]/50 !text-[#00FF66] !bg-[#00FF66]/10" : "!border-[#FF003C]/50 !text-[#FF003C] !bg-[#FF003C]/10"}`}>
                  {r.status.toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-white/70">"{r.reason}"</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">+{r.extension_hours}h · {new Date(r.created_at).toLocaleString("id-ID")}</p>
            </div>
          ))}
        </div>
      ))}

      {/* Order chat modal */}
      {chatModal && (
        <OrderChatPanel order={chatModal} currentUserId={user.id} onClose={() => setChatModal(null)} />
      )}

      {/* Complain modal */}
      {complainModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="complain-modal">
          <div className="w-full max-w-md border border-[#FF003C]/40 bg-[#0a0a0a] p-6">
            <h2 className="font-display font-black text-xl text-white">Komplain Pesanan</h2>
            <p className="text-white/60 text-sm mt-2">{complainModal.items?.[0]?.title}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#FF003C] mt-1">⚠ Pencairan saldo seller akan ditahan sampai admin review</p>
            <form onSubmit={submitComplain} className="mt-4">
              <label className="text-xs uppercase tracking-widest text-white/60">Jelaskan masalahnya (min 10 karakter)</label>
              <textarea
                data-testid="complain-reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={5}
                placeholder="Contoh: Akun tidak bisa login, password salah, akun sudah di-banned, dll."
                className="w-full bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm p-3 mt-2"
              />
              <div className="mt-4 flex gap-2">
                <button data-testid="complain-submit" type="submit" className="flex-1 px-4 py-3 bg-[#FF003C] text-white font-display font-black uppercase tracking-widest text-xs">Kirim Komplain</button>
                <button type="button" onClick={() => { setComplainModal(null); setReason(""); }} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extend garansi modal */}
      {extendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="extend-modal">
          <div className="w-full max-w-md border border-[#FFD700]/40 bg-[#0a0a0a] p-6">
            <h2 className="font-display font-black text-xl text-white">Perpanjang Garansi +7 Hari</h2>
            <p className="text-white/60 text-sm mt-2">{extendModal.title || extendModal.items?.[0]?.title}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
              {extendModal.kind === "order"
                ? `Sisa kuota perpanjangan: ${2 - Number(extendModal.extended_count || 0)}/2 · Langsung diperpanjang otomatis`
                : "Akan direview admin Ustore"}
            </p>
            <form onSubmit={submitExtend} className="mt-4">
              <label className="text-xs uppercase tracking-widest text-white/60">Alasan (min 5 karakter)</label>
              <textarea
                data-testid="extend-reason"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Saya butuh waktu lebih untuk verifikasi..."
                className="w-full bg-black border border-white/15 focus:border-[#FFD700] text-white text-sm p-3 mt-2"
              />
              <div className="mt-4 flex gap-2">
                <button data-testid="extend-submit" type="submit" className="flex-1 px-4 py-3 bg-[#FFD700] text-black font-display font-black uppercase tracking-widest text-xs">{extendModal.kind === "order" ? "Perpanjang Sekarang" : "Kirim Request"}</button>
                <button type="button" onClick={() => { setExtendModal(null); setReason(""); }} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="review-modal">
          <div className="w-full max-w-md border border-[#FFD700]/40 bg-[#0a0a0a] p-6">
            <h2 className="font-display font-black text-xl text-white">Berikan Ulasan</h2>
            <p className="text-white/60 text-sm mt-2">{reviewModal.items?.[0]?.title}</p>
            <form onSubmit={submitReview} className="mt-4">
              <label className="text-xs uppercase tracking-widest text-white/60">
                Rating <span className="text-[#FF003C]">*</span>
              </label>
              <div className="flex gap-1 mt-2" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    data-testid={`review-star-${star}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${star} bintang`}
                  >
                    <Star
                      className="w-8 h-8"
                      fill={(hoverRating || rating) >= star ? "#FFD700" : "none"}
                      stroke={(hoverRating || rating) >= star ? "#FFD700" : "#ffffff55"}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-[10px] uppercase tracking-widest text-[#FFD700] mt-1" data-testid="review-rating-value">
                  {rating} dari 5 bintang
                </p>
              )}

              <label className="text-xs uppercase tracking-widest text-white/60 block mt-4">
                Ulasan <span className="text-white/40">(opsional)</span>
              </label>
              <textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Ceritakan pengalamanmu dengan produk ini..."
                className="w-full bg-black border border-white/15 focus:border-[#FFD700] text-white text-sm p-3 mt-2"
              />

              <div className="mt-4 flex gap-2">
                <button
                  data-testid="review-submit"
                  type="submit"
                  disabled={rating < 1}
                  className="flex-1 px-4 py-3 bg-[#FFD700] text-black font-display font-black uppercase tracking-widest text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Kirim Ulasan
                </button>
                <button type="button" onClick={() => { setReviewModal(null); setRating(0); setHoverRating(0); setComment(""); }} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
